import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  RefreshCw,
} from "lucide-react";
import TaskPopupWaiting from "../../../modules/hrm/tasks/components/popups/TaskPopupWaitingNew";
import TaskPopupReported from "../../../modules/hrm/tasks/components/popups/TaskPopupReportedNew";
import TaskPopupApproved from "../../../modules/hrm/tasks/components/popups/TaskPopupApprovedNew";
import {
  MobilePageShell,
  MobilePageHeader,
} from "../MobileNavPrimitives";
import {
  WORK_LIST_MODES,
  loadEnrichedMonthTasks,
  statusMeta,
  taskTitle,
  todayIso,
} from "./workTaskUtils";
import { reportTask } from "../../../api/hrm/tasks/task_info";

function formatWorkDay(day) {
  if (!day) return "";
  try {
    return format(new Date(day), "dd/MM/yyyy", { locale: vi });
  } catch {
    return String(day);
  }
}

export default function MobileWorkListScreen() {
  const { mode } = useParams();
  const navigate = useNavigate();
  const config = WORK_LIST_MODES[mode] || WORK_LIST_MODES["need-approve"];

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState("");
  const [popup, setPopup] = useState(null);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const load = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError("");
      try {
        const enriched = await loadEnrichedMonthTasks(currentUser?.id);
        setTasks(enriched);
      } catch (e) {
        console.error(e);
        setError("Không tải được danh sách. Thử lại sau.");
        setTasks([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [currentUser?.id]
  );

  useEffect(() => {
    load(false);
  }, [load, mode]);

  const today = todayIso();
  const filtered = useMemo(
    () => config.filter(tasks, today),
    [config, tasks, today]
  );

  const openTask = (task) => {
    const status = String(task.status || "").toLowerCase();
    setPopup({
      status,
      employeeId: task.employee_id,
      employeeName: task.employee_name,
      branchName: task.branch_name,
      workDay: String(task.work_day || "").slice(0, 10),
      taskList: task.task_list || [],
    });
  };

  const closePopup = () => setPopup(null);

  const handleReport = async (task, payload) => {
    await reportTask(task.id, payload);
  };

  return (
    <MobilePageShell>
      <MobilePageHeader
        title={config.title}
        subtitle={config.subtitle}
        tone={config.tone}
        trailing={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => load(true)}
              disabled={refreshing || loading}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-rose-100 bg-rose-50 text-rose-700 active:bg-rose-100 disabled:opacity-50"
              aria-label="Làm mới"
            >
              <RefreshCw
                size={16}
                className={refreshing ? "animate-spin" : undefined}
              />
            </button>
          </div>
        }
      >
        <button
          type="button"
          onClick={() => navigate("/m/work")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] font-medium text-gray-700 active:bg-gray-50"
        >
          <ArrowLeft size={14} />
          Về Công việc
        </button>
      </MobilePageHeader>

      <div className="px-5 pt-4 pb-4">
        {error ? (
          <div className="mb-3 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
            {error}
          </div>
        ) : null}

        <p className="mb-3 text-[12px] text-gray-500">
          {loading ? "Đang tải…" : `${filtered.length} công việc`}
        </p>

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          {loading ? (
            <div className="space-y-3 p-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-xl bg-gray-100"
                />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <ul className="divide-y divide-gray-50">
              {filtered.map((task) => {
                const meta = statusMeta(task.status);
                return (
                  <li key={`${task.id}-${task.status}-${task.work_day}`}>
                    <button
                      type="button"
                      onClick={() => openTask(task)}
                      className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                    >
                      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
                        <ClipboardList size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800">
                          {taskTitle(task)}
                        </p>
                        <p className="mt-0.5 truncate text-[11px] text-gray-400">
                          {[
                            task.employee_name,
                            formatWorkDay(task.work_day),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                        <span
                          className={`mt-1.5 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.chip}`}
                        >
                          {meta.label}
                        </span>
                      </div>
                      <ChevronRight
                        size={14}
                        className="mt-2 shrink-0 text-gray-300"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="flex flex-col items-center px-6 py-12 text-center">
              <CheckCircle2 className="mb-2 text-emerald-500" size={28} />
              <p className="text-[13px] text-gray-400">{config.empty}</p>
            </div>
          )}
        </div>
      </div>

      {popup?.status === "waiting" ? (
        <TaskPopupWaiting
          isOpen
          onClose={closePopup}
          employeeId={popup.employeeId}
          employeeName={popup.employeeName}
          branchName={popup.branchName}
          workDay={popup.workDay}
          taskList={popup.taskList}
          onUpdated={() => {
            closePopup();
            load(true);
          }}
        />
      ) : null}
      {popup?.status === "reported" ? (
        <TaskPopupReported
          isOpen
          onClose={closePopup}
          employeeId={popup.employeeId}
          employeeName={popup.employeeName}
          branchName={popup.branchName}
          workDay={popup.workDay}
          taskList={popup.taskList}
          onUpdated={() => {
            closePopup();
            load(true);
          }}
        />
      ) : null}
      {popup?.status === "approved" ? (
        <TaskPopupApproved
          isOpen
          onClose={closePopup}
          employeeId={popup.employeeId}
          employeeName={popup.employeeName}
          branchName={popup.branchName}
          workDay={popup.workDay}
          taskList={popup.taskList}
          onReport={handleReport}
          onUpdated={() => {
            closePopup();
            load(true);
          }}
        />
      ) : null}
    </MobilePageShell>
  );
}
